import React from "react";
import { Box, Divider, Option } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import type { TrainingTemplate } from "../../../../api/training/Training.typed";
import { TrainingStatusCode } from "../../../../api/training/Training.typed";
import TrainingsQualificationSelect from "../../common/TrainingsQualificationSelect";
import ActiveAlertChip from "../../../common/ActiveAlertChip";
import { ctaAccentColor, trainingsStatusSelectSx } from "../../../../styles/globalStyles";

interface TrainingsListHeaderProps {
  qualifications: TrainingTemplate[] | undefined;
  templateId: string;
  onTemplateIdChange: (value: string | null) => void;
  status: number;
  onStatusChange: (value: number) => void;
  isLoading: boolean;
  searchInput: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  alertName?: string;
  onClearAlert?: () => void;
}

const STATUS_OPTIONS: { value: TrainingStatusCode; emoji: string; label: string }[] = [
  { value: TrainingStatusCode.InProgress, emoji: "🟢", label: "En cours" },
  { value: TrainingStatusCode.Validated, emoji: "✅", label: "Validées" },
  { value: TrainingStatusCode.Cancelled, emoji: "❌", label: "Annulées" },
];

const statusOptionContentSx: SxProps = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1.25,
};

// Responsive row: column on mobile, three slots (left/middle/right) on larger screens.
const rowSx: SxProps = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "space-between",
  gap: 1,
  flexWrap: "wrap",
};

const searchInputSx: SxProps = {
  minWidth: { xs: "100%", sm: 300 },
  maxWidth: "100%",
  "--dark-input-color": ctaAccentColor,
  "--dark-input-placeholder": ctaAccentColor,
};

// Keeps the qualification select centered when the search slot is empty.
const searchPlaceholderSx: SxProps = {
  display: { xs: "none", sm: "block" },
  minWidth: 300,
};

const TrainingsListHeader: React.FC<TrainingsListHeaderProps> = (props) => {
  const {
    qualifications,
    templateId,
    onTemplateIdChange,
    status,
    onStatusChange,
    isLoading,
    searchInput,
    onSearchChange,
    alertName,
    onClearAlert,
  } = props;

  const showSearch = Boolean(templateId) && !isLoading;

  return (
    <Box sx={{ flexShrink: 0, px: 2, pt: 1, pb: 2 }}>
      <Box sx={rowSx}>
        <DarkSelect
          value={status}
          onChange={(_, value) => {
            if (value != null) onStatusChange(Number(value));
          }}
          placeholder="Statut"
          aria-label="Filtrer par statut"
          sx={trainingsStatusSelectSx}
          renderValue={(selected) => {
            const opt = STATUS_OPTIONS.find((o) => o.value === selected?.value);
            if (!opt) return null;
            return (
              <Box component="span" sx={statusOptionContentSx}>
                <span>{opt.emoji}</span>
                <span>{opt.label}</span>
              </Box>
            );
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              <Box component="span" sx={statusOptionContentSx}>
                <span>{opt.emoji}</span>
                <span>{opt.label}</span>
              </Box>
            </Option>
          ))}
        </DarkSelect>

        <TrainingsQualificationSelect
          templates={qualifications}
          value={templateId}
          onChange={onTemplateIdChange}
        />

        {showSearch ? (
          <DarkInput
            placeholder="Rechercher"
            value={searchInput}
            onChange={onSearchChange}
            sx={searchInputSx}
          />
        ) : (
          <Box aria-hidden sx={searchPlaceholderSx} />
        )}
      </Box>
      {alertName && onClearAlert ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          <ActiveAlertChip alertName={alertName} onClear={onClearAlert} />
        </Box>
      ) : null}
      <Divider sx={{ marginTop: "10px" }} />
    </Box>
  );
};

export default React.memo(TrainingsListHeader);
